import api from './api';

/**
 * AI Service for handling AI auto-fill functionality
 */
class AIService {
    /**
     * Process AI prompt to suggest form field values with streaming support
     * @param {string} prompt - The user's prompt describing patient information
     * @param {Object} formContext - Form context (type, transferType, etc.)
     * @param {string} facilityId - Facility ID
     * @param {string} accountId - Account ID
     * @param {Array} activeFacilities - Array of active facility IDs for AI filtering
     * @returns {Promise<Object>} AI response with field suggestions
     */
    async processFormAutoFill(prompt, formContext, facilityId, accountId, activeFacilities = []) {
        try {
            // Use axios instance for task-based polling approach
            console.log('📤 Sending form autofill request to backend AI with polling...');

            const response = await api.post('/api/ai-autofill/form-fields', {
                prompt: prompt.trim(),
                formContext,
                facilityId,
                accountId,
                activeFacilities
            });

            const data = response;
            console.log('🔍 AI Service - Response:', data);

            const pollTaskResult = async (taskId) => {
                console.log(`🔄 Starting autofill polling for taskId: ${taskId}`);
                const maxWait = 12 * 60 * 1000; // 12 minutes (longer than backend's 10-minute timeout)
                const start = Date.now();
                let intervalId = null;
                
                // CRITICAL: Add initial delay to give backend time to initialize task
                console.log(`⏳ Waiting 800ms before first poll to ensure task initialization...`);
                await new Promise(resolve => setTimeout(resolve, 800));

                return new Promise((resolve, reject) => {
                    // Helper to safely clear interval
                    const safeCleanup = () => {
                        if (intervalId) {
                            clearInterval(intervalId);
                            intervalId = null;
                        }
                        console.log(`🧹 Autofill polling interval cleaned up for task: ${taskId}`);
                    };
                    
                    intervalId = setInterval(async () => {
                        try {
                            // Check timeout
                            if (Date.now() - start > maxWait) {
                                console.log('⏰ Autofill polling timed out');
                                safeCleanup();
                                return reject(new Error('AI autofill processing timed out. Please try again.'));
                            }
                            
                            console.log(`📡 Polling autofill taskId: ${taskId}`);
                            const response = await api.get(`/api/ai-autofill/form-fields/${taskId}`);
                            const data = response;
                            console.log(`📥 Autofill API Response:`, data);

                            if (data.status === 'complete') {
                                console.log('✅ Autofill task completed, stopping polling');
                                console.log(`✅ Autofill task was polled ${data.pollCount || 'unknown'} times`);
                                safeCleanup();
                                resolve({ success: true, data: data.finalResponse });
                            } else if (data.status === 'failed') {
                                console.log('❌ Autofill task failed, stopping polling');
                                console.log(`❌ Failure reason: ${data?.result || 'Unknown error'}`);
                                safeCleanup();
                                reject(new Error(data?.result || 'AI autofill processing failed'));
                            } else {
                                console.log(`⏳ Autofill task still processing, status: ${data.status}`);
                            }

                        } catch (err) {
                            console.error('🚨 Error in autofill polling interval:', err);
                            console.error('🚨 Error details:', {
                                message: err.message,
                                status: err.response?.status,
                                taskId: taskId
                            });
                            
                            // Handle network errors - continue polling for a bit
                            if (!err.response) {
                                console.error('🚨 Network error during autofill polling, will retry...');
                                return; // Continue polling
                            }
                            
                            // For other errors, stop polling
                            console.error('🚨 Stopping autofill polling due to error');
                            safeCleanup();
                            reject(err);
                        }
                    }, 2000);
                });
            };

            const finalResult = await pollTaskResult(data.taskId);

            if (finalResult?.success) {
                return finalResult.data;
            } else {
                throw new Error('AI service returned an invalid response');
            }
        } catch (error) {
            console.error('AI Service Error:', error);
            throw new Error(
                error.message || 
                'Failed to process AI request. Please try again.'
            );
        }
    }

    /**
     * Get available form fields for AI auto-fill
     * @param {Object} formContext - Form context (type, transferType, etc.)
     * @param {string} facilityId - Facility ID
     * @param {string} accountId - Account ID
     * @param {Array} activeFacilities - Array of active facility IDs for AI filtering
     * @returns {Promise<Array>} Available form fields
     */
    async getAvailableFields(formContext, facilityId, accountId, activeFacilities = []) {
        try {
            const response = await api.get('/api/ai-autofill/available-fields', {
                params: {
                    ...formContext,
                    facilityId,
                    accountId,
                    activeFacilities: activeFacilities.join(',') // Convert array to comma-separated string for URL params
                }
            });

            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response?.error || 'Failed to get available fields');
            }
        } catch (error) {
            console.error('Get Available Fields Error:', error);
            throw new Error(
                error.response?.data?.error || 
                'Failed to get available fields. Please try again.'
            );
        }
    }

    /**
     * Verify AI description with OpenAI (if available)
     * @param {string} aiDescription - AI description to verify
     * @param {string} fieldLabel - Field label
     * @param {string} fieldType - Field type
     * @returns {Promise<Object>} Verification result
     */
    async verifyAIDescription(aiDescription, fieldLabel, fieldType) {
        try {
            const response = await api.post('/api/ai-autofill/verify-ai-description', {
                aiDescription,
                fieldLabel,
                fieldType
            });

            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response?.error || 'Failed to verify AI description');
            }
        } catch (error) {
            console.error('Verify AI Description Error:', error);
            throw new Error(
                error.response?.data?.error || 
                'Failed to verify AI description. Please try again.'
            );
        }
    }

    /**
     * Update AI settings for a specific question
     * @param {string} questionId - Question ID
     * @param {Object} aiSettings - AI settings object
     * @returns {Promise<Object>} Updated AI settings
     */
    async updateAISettings(questionId, aiSettings) {
        try {
            const response = await api.post(`/api/ai-autofill/ai-settings/${questionId}`, aiSettings);

            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response?.error || 'Failed to update AI settings');
            }
        } catch (error) {
            console.error('Update AI Settings Error:', error);
            throw new Error(
                error.response?.data?.error || 
                'Failed to update AI settings. Please try again.'
            );
        }
    }

    /**
     * Get AI settings for a specific question
     * @param {string} questionId - Question ID
     * @returns {Promise<Object>} AI settings
     */
    async getAISettings(questionId) {
        try {
            const response = await api.get(`/api/ai-autofill/ai-settings/${questionId}`);

            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response?.error || 'Failed to get AI settings');
            }
        } catch (error) {
            console.error('Get AI Settings Error:', error);
            throw new Error(
                error.response?.data?.error || 
                'Failed to get AI settings. Please try again.'
            );
        }
    }

    /**
     * Chat with AI to improve prompt
     * @param {string} message - User message
     * @param {string} aiDescription - Current AI description
     * @param {string} fieldLabel - Field label
     * @param {string} fieldType - Field type
     * @param {Array} conversationHistory - Previous conversation history
     * @returns {Promise<Object>} AI chat response
     */
    async chatWithAI(message, aiDescription, fieldLabel, fieldType, conversationHistory = []) {
        try {
            const response = await api.post('/api/ai-autofill/chat-with-ai', {
                message,
                aiDescription,
                fieldLabel,
                fieldType,
                conversationHistory
            });

            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response?.error || 'Failed to chat with AI');
            }
        } catch (error) {
            console.error('AI Chat Error:', error);
            throw new Error(
                error.response?.data?.error || 
                'Failed to chat with AI. Please try again.'
            );
        }
    }

    /**
     * Test AI prompt with sample data
     * @param {string} aiDescription - AI description to test
     * @param {string} fieldLabel - Field label
     * @param {string} fieldType - Field type
     * @param {string} testData - Sample data to test with
     * @param {string} facilityId - Facility ID
     * @param {string} accountId - Account ID
     * @returns {Promise<Object>} Test result
     */
    async testAIPrompt(aiDescription, fieldLabel, fieldType, testData, facilityId, accountId) {
        try {
            const response = await api.post('/api/ai-autofill/test-ai-prompt', {
                aiDescription,
                fieldLabel,
                fieldType,
                testData,
                facilityId,
                accountId
            });

            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response?.error || 'Failed to test AI prompt');
            }
        } catch (error) {
            console.error('Test AI Prompt Error:', error);
            throw new Error(
                error.response?.data?.error || 
                'Failed to test AI prompt. Please try again.'
            );
        }
    }

    /**
     * Test AI auto-fill for a specific question
     * @param {string} questionId - Question ID to test
     * @param {string} description - AI description for the question
     * @param {string} testData - Sample data to test with
     * @param {string} facilityId - Facility ID
     * @param {string} accountId - Account ID
     * @param {Array} activeFacilities - Active facilities array
     * @returns {Promise<Object>} Test result
     */
    async testQuestion(questionId, description, testData, facilityId, accountId, activeFacilities = []) {
        try {
            const response = await api.post('/api/ai-autofill/test-question', {
                questionId,
                description,
                testData,
                facilityId,
                accountId,
                activeFacilities
            });

            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response?.error || 'Failed to test question');
            }
        } catch (error) {
            console.error('Error testing question:', error);
            throw new Error(
                error?.response?.data?.error || 
                'Failed to test question. Please try again.'
            );
        }
    }

    /**
     * Explain why a specific field suggestion was made
     * @param {Object} params - Parameters for explanation
     * @param {string} params.fieldAccessor - Field accessor
     * @param {string} params.fieldLabel - Field label
     * @param {string} params.suggestedValue - Suggested value
     * @param {string} params.originalPrompt - Original user prompt
     * @param {Object} params.formContext - Form context
     * @param {string} params.facilityId - Facility ID
     * @param {string} params.accountId - Account ID
     * @returns {Promise<Object>} Explanation with highlighted text portions
     */
    async explainFieldSuggestion({
        fieldAccessor,
        fieldLabel,
        suggestedValue,
        originalPrompt,
        formContext,
        facilityId,
        accountId
    }) {
        try {
            const response = await api.post('/api/ai-autofill/explain-field', {
                fieldAccessor,
                fieldLabel,
                suggestedValue,
                originalPrompt,
                formContext,
                facilityId,
                accountId
            });

            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response?.error || 'Failed to get field explanation');
            }
        } catch (error) {
            console.error('Explain Field Error:', error);
            throw new Error(
                error.response?.data?.error || 
                'Failed to get field explanation. Please try again.'
            );
        }
    }
}

// Create and export singleton instance
const aiService = new AIService();
export default aiService;
